/**
 * Where someone is connecting from, and which of Connect's two surfaces we send
 * them to.
 *
 * Vocabulary this file depends on: **Soku Connect** is the product and names
 * neither surface; the **MCP connection** is the endpoint an AI app is pointed
 * at; the **Soku CLI** is the other way in. See `cli-surface.ts` for why that
 * distinction is load-bearing rather than cosmetic.
 *
 * This table exists because the console used to answer "how do I connect" three
 * separate times — a short endpoint card on Home, a seven-tab MCP guide on AI
 * apps, and a CLI section below it, with a collapsed "using the CLI instead?"
 * panel in between. Three renderings meant three chances to drift, and the
 * collapsed panel was the only place the actual choice was ever made. Now the
 * choice is made once, here, and the UI is two densities of one component
 * reading this table.
 *
 * NOT a new source of copy. Every string a reader follows still comes from the
 * two documents that already own them — `hosts.ts` for the MCP side,
 * `cli-surface.ts` (re-exporting `lib/marketing/cli-onboarding.ts`, which also
 * drives `soku.ai/cli` and the agent manual at `soku.ai/cli/skill.md`) for the
 * CLI side. This file only composes and routes. A third copy of the setup
 * instructions is exactly the one that would go stale unnoticed.
 *
 * Like `CONNECT_HOSTS`, this is a set of WORKED EXAMPLES, not an allowlist.
 * Nothing in the product consults it to decide whether a client may connect.
 */

import { CONNECT_HOSTS, connectHostsForProduct, type ConnectHost } from './hosts'
import type { ProductBrand } from '@/lib/product-hosts'

export type ConnectSurface = 'cli' | 'mcp'

export interface ConnectClient {
  id: string
  name: string
  /**
   * The surfaces this client can actually use.
   *
   * `mcp` carries the host entry, because the MCP instructions genuinely differ
   * per client (VS Code's wrapper key is `servers`, Cursor's entry carries no
   * `type`, and so on). `cli` is a plain `true` because the CLI guide is ONE
   * document for every client — the only per-client fact is whether there is a
   * shell to run it in. Modelling it as a per-client guide would invite someone
   * to write a second copy of `CLI_HUMAN_STEPS` here.
   */
  surfaces: { mcp?: ConnectHost; cli?: true }
  /**
   * Which surface we put first for this client.
   *
   * `null` means "genuinely the reader's call, and we render both as peers" —
   * used only where we have no basis to choose, which today is the
   * bring-your-own-agent entry. It is deliberately not a way to avoid deciding:
   * a client with exactly one surface may never be `null`, and
   * `connect-client-routing.test.ts` asserts that.
   */
  recommended: ConnectSurface | null
  /**
   * Why, in one sentence, always rendered next to the recommendation.
   *
   * A recommendation with no visible reason reads as an arbitrary house
   * preference, which is precisely what a reader overrides at random.
   */
  whyRecommended: string
}

function host(
  id: string,
  hosts: readonly ConnectHost[] = CONNECT_HOSTS,
): ConnectHost {
  const entry = hosts.find(candidate => candidate.id === id)
  // A missing id is a build-time authoring error in this file, not a runtime
  // state: throwing here surfaces it in the first test that imports the table,
  // rather than rendering a client whose "Add it" section is silently blank.
  if (!entry) throw new Error(`clients.ts references unknown host: ${id}`)
  return entry
}

/**
 * The routing table.
 *
 * The two CLI-first entries are the shell-native agents, and the reasons are
 * the ones their own MCP guides already document as pitfalls: `claude mcp add`
 * writes configuration without signing in, and non-interactive runs (`claude
 * -p`, Agent SDK, CI) have no panel in which to authorize at all. Add to that
 * the two things only the CLI can do — act on an account behind your approval,
 * and install task skills — and the terminal reader is better served there.
 *
 * Cursor and VS Code stay MCP-first even though their agents can run a shell:
 * both configure MCP inside their own settings, which is where their users and
 * their team-sharing features already expect it. The CLI is offered as a peer,
 * not hidden.
 */
export const CONNECT_CLIENTS: readonly ConnectClient[] = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    surfaces: { mcp: host('claude-code'), cli: true },
    recommended: 'cli',
    whyRecommended:
      'One sign-in covers every project, and headless runs (`claude -p`, CI, the Agent SDK) work without a browser — `claude mcp add` only writes config, so a non-interactive run can never finish the MCP authorization. Both surfaces can act on your accounts once you approve each change.',
  },
  {
    id: 'codex',
    name: 'Codex',
    surfaces: { mcp: host('codex'), cli: true },
    recommended: 'cli',
    whyRecommended:
      'Signing in once leaves the session on the machine, so sandboxed and non-interactive runs keep working. Both surfaces can write behind your own approval; only the CLI reaches skills, context and memory.',
  },
  {
    id: 'cursor',
    name: 'Cursor',
    surfaces: { mcp: host('cursor'), cli: true },
    recommended: 'mcp',
    whyRecommended:
      'Cursor manages connectors in its own settings, which is where its project and team sharing expect them. Its agent can run the Soku CLI too — choose that if you want writes or unattended runs.',
  },
  {
    id: 'vscode',
    name: 'VS Code',
    surfaces: { mcp: host('vscode'), cli: true },
    recommended: 'mcp',
    whyRecommended:
      'VS Code keeps MCP servers in its own `mcp.json`, so a workspace can commit the connection for everyone. Its terminal runs the Soku CLI too — choose that if you want writes or unattended runs.',
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    surfaces: { mcp: host('chatgpt') },
    recommended: 'mcp',
    whyRecommended:
      'ChatGPT has no terminal, so the MCP connection is the only way in.',
  },
  {
    id: 'claude-web',
    name: 'Claude (web & desktop)',
    surfaces: { mcp: host('claude-web') },
    recommended: 'mcp',
    whyRecommended:
      'The web and desktop apps have no terminal, so the MCP connection is the only way in. Working in Claude Code instead? That one has a shell — start there.',
  },
  {
    id: 'custom',
    name: 'Your own agent',
    surfaces: { mcp: host('custom'), cli: true },
    // The one honest `null`: we do not know what you built.
    recommended: null,
    whyRecommended:
      'Pick by what your agent already has. An MCP client gets typed tools, a price quote before every paid call and a per-app spending limit. A shell gets the Soku CLI, writes included, behind your approval.',
  },
]

/** The client a `?client=` / legacy `?host=` value names, falling back to the first. */
export function findClient(
  id: string | null | undefined,
  product: ProductBrand['product'] = 'soku',
): ConnectClient {
  // Falls back rather than returning null: a stale link someone pasted to a
  // colleague should open a guide, not an empty panel.
  const client =
    CONNECT_CLIENTS.find(candidate => candidate.id === id) ?? CONNECT_CLIENTS[0]
  if (product === 'soku' || !client.surfaces.mcp) return client
  const mcp = host(client.surfaces.mcp.id, connectHostsForProduct(product))
  return { ...client, surfaces: { ...client.surfaces, mcp } }
}

/** The surfaces a client supports, recommended one first. */
export function orderedSurfaces(client: ConnectClient): ConnectSurface[] {
  const available = surfaceTabOrder(client)
  if (!client.recommended) return available
  return [
    client.recommended,
    ...available.filter(surface => surface !== client.recommended),
  ]
}

/**
 * The order the surface tabs are DRAWN in — MCP first, always, wherever both
 * exist.
 *
 * This used to be `orderedSurfaces`, and that one function was answering two
 * questions: which surface a reader lands on, and where each tab sits. Tying
 * them together meant the tab strip re-ordered itself as the reader arrowed
 * across the app row — "MCP connection" was on the right under Claude Code and
 * on the left under ChatGPT — so the control moved out from under the pointer
 * of anyone comparing two apps. Worse, a page that opens on MCP (the source
 * pages do) then showed its selected tab in second position, which reads as a
 * tab that failed to activate.
 *
 * Position is a spatial contract and belongs to the strip; the recommendation
 * is editorial and belongs to the client. `orderedSurfaces` still carries the
 * second, and `connect-client-routing.test.ts` still holds the shell-native
 * agents to leading with the CLI — they lead with it by being SELECTED on it,
 * not by having their tab moved.
 */
export function surfaceTabOrder(client: ConnectClient): ConnectSurface[] {
  const available: ConnectSurface[] = []
  if (client.surfaces.mcp) available.push('mcp')
  if (client.surfaces.cli) available.push('cli')
  return available
}

/** What the surface is called on screen. Never "Connect" — that is the product. */
export const SURFACE_LABEL: Record<ConnectSurface, string> = {
  mcp: 'MCP connection',
  cli: 'Soku CLI',
}
